import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Loader2 } from 'lucide-react'
import {
	data,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
	Form,
	Link,
	useActionData,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { comboAtivo, login, requireAnonymous } from '#app/utils/auth.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return null
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Email ou senha incorretos',
					})
					return z.NEVER
				}
				const comboId = await comboAtivo(session.userId)
				if (!comboId) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message:
							'Você não possui nenhum combo ativo no momento ! (acesso expirado)',
					})
					return z.NEVER
				}
				return { ...data, session, comboId }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo, comboId } = submission.value

	return handleNewSession({
		request,
		session,
		comboId,
		remember: remember ?? false,
		redirectTo: redirectTo || '/home',
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const isPendingGoogle = useIsPending({ formAction: '/auth/google' })

	return (
		<div className="w-full px-4 md:px-0">
			<div className="flex flex-col space-y-4 md:space-y-5">
				<h2 className="text-xl font-bold md:text-2xl">Faça o login</h2>
				{/* Botão Google */}
				<form action="/auth/google" method="POST">
					<button
						type="submit"
						disabled={isPendingGoogle}
						className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-card px-4 py-2.5 text-sm font-medium  shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-background md:px-24 md:text-base"
					>
						{isPendingGoogle ? (
							<>
								<Loader2 className="h-5 w-5 animate-spin" />
								<span>Processando...</span>
							</>
						) : (
							<>
								<Icon name="google-logo" className="h-5 w-5" />
								<span>Entrar com o Google</span>
							</>
						)}
					</button>
				</form>

				{/* Divisor */}
				<div className="relative py-2">
					<div
						className="absolute inset-0 flex items-center"
						aria-hidden="true"
					>
						<div className="w-full border-t border-gray-300" />
					</div>
					<div className="relative flex justify-center">
						<span className="bg-card px-4 text-sm text-gray-500">OU</span>
					</div>
				</div>
			</div>
			<Form method="post" {...getFormProps(form)} className="mt-4">
				<div className="space-y-3 md:space-y-4">
					<HoneypotInputs />
					<Field
						labelProps={{
							children: 'Email',
							className: 'text-sm md:text-base',
						}}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
							autoFocus: true,

							className: 'lowercase w-full p-2 text-sm md:text-base rounded-md',
							autoComplete: 'email',
						}}
						errors={fields.email.errors}
					/>
					<Field
						labelProps={{
							children: 'Senha',
							className: 'text-sm md:text-base',
						}}
						inputProps={{
							...getInputProps(fields.password, {
								type: 'password',
							}),
							autoComplete: 'current-password',

							className: 'w-full p-2 text-sm md:text-base rounded-md',
						}}
						errors={fields.password.errors}
					/>
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<CheckboxField
							labelProps={{
								htmlFor: fields.remember.id,
								children: 'Continuar conectado',

								className: 'text-sm md:text-base',
							}}
							buttonProps={getInputProps(fields.remember, {
								type: 'checkbox',
							})}
							errors={fields.remember.errors}
						/>
						<Link
							to="/forgot-password"
							className="text-sm font-semibold hover:text-primary md:text-base"
						>
							Esqueceu a senha?
						</Link>
					</div>

					<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					<div className="flex items-center justify-between pt-4">
						<StatusButton
							data-testid="login-button"
							className="w-full py-2.5 text-sm md:text-base"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Entrar
						</StatusButton>
					</div>
				</div>
			</Form>
		</div>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login | JusMemoriza' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
